import React, { useState, useEffect, useCallback } from "react";
import { Popconfirm } from "antd";
import "../App.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8011";

const ExpenseList = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterDate, setFilterDate] = useState("");
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState("");
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState("");
  const [userNames, setUserNames] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentExpense, setCurrentExpense] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // Delete a expense by ID
  const deleteExpenseById = async (id) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        `${API_BASE_URL}/expenses/deleteExpense/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to delete expense");
      toast.success("Expense deleted successfully");
      // Refresh the expense list after deletion
      fetchExpenses();
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast.error("Failed to delete expense");
    }
  };

  // Handle modal opening
  const openModal = (expense) => {
    setCurrentExpense(expense);
    setIsModalOpen(true);
  };

  // Handle modal closing
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentExpense(null);
  };

  // Handle file upload for image
  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const uploadImage = async (image) => {
    const data = new FormData();
    data.append("file", image);
    data.append("upload_preset", "DecentBuilders");
    try {
      const response = await fetch(
        "https://api.cloudinary.com/v1_1/dwbwxh3sj/image/upload",
        {
          method: "POST",
          body: data,
        }
      );
      const urlData = await response.json();
      return urlData.url;
    } catch (error) {
      console.error(error);
      toast.error("Image upload failed.");
      return null;
    }
  };
  
  const updateExpense = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      let imageUrl = currentExpense.imagePath || [];

      if (imageFile && imageFile.length > 0) {
        const uploadedImageUrls = await Promise.all(
          Array.from(imageFile).map((image) => uploadImage(image))
        );
        imageUrl = [...imageUrl, ...uploadedImageUrls];
      }

      const updatedExpenseData = {
        title: currentExpense.title,
        description: currentExpense.description,
        price: currentExpense.price,
        category: currentExpense.category,
        project: currentExpense.project,
        date: currentExpense.date,
        imagePath: imageUrl,
      };

      const response = await fetch(
        `${API_BASE_URL}/expenses/updateExpense/${currentExpense._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedExpenseData),
        }
      );

      if (!response.ok) throw new Error("Failed to update expense");

      toast.success("Expense updated successfully");
      closeModal();
      fetchExpenses();
    } catch (error) {
      console.error("Error updating expense:", error);
      toast.error("Failed to update expense");
    }
  };

  const fetchUserNames = useCallback(async (userIds) => {
    try {
      const userNames = {};
      for (const id of userIds) {
        const response = await fetch(`${API_BASE_URL}/auth/users/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user name for ID ${id}`);
        }
        const data = await response.json();
        userNames[id] = data.name;
      }
      setUserNames(userNames);
    } catch (error) {
      console.error("Error fetching user names:", error.message);
      toast.error(`Failed to load user names: ${error.message}`);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(`${API_BASE_URL}/expenses/getAllExpenses`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch expenses");
      const expenseData = await response.json();
      const sortedExpenses = expenseData.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      setExpenses(sortedExpenses);
      setFilteredExpenses(sortedExpenses);

      const userIds = [...new Set(expenseData.map((exp) => exp.userId))];
      fetchUserNames(userIds);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [fetchUserNames]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/categories/getAllCategories`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      if (Array.isArray(data)) {
        setCategories(data);
      } else {
        throw new Error("Categories data is not an array");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  }, []);
  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/projects/getAllProjects`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch projects");
      const data = await response.json();
      if (Array.isArray(data)) {
        setProjects(data);
      } else {
        throw new Error("Projects data is not an array");
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchProjects();
  }, [fetchExpenses, fetchCategories, fetchProjects]);

  const handleFilterDateChange = (event) => {
    const selectedDate = event.target.value;
    setFilterDate(selectedDate);
    filterExpenses(selectedDate, filterCategory, filterProject);
  };
  const handleFilterCategoryChange = (event) => {
    const selectedCategory = event.target.value;
    setFilterCategory(selectedCategory);
    filterExpenses(filterDate, selectedCategory, filterProject);
  };
  const handleFilterProjectChange = (event) => {
    const selectedProject = event.target.value;
    setFilterProject(selectedProject);
    filterExpenses(filterDate, filterCategory, selectedProject);
  };

  const filterExpenses = (date, category, project) => {
    let filtered = expenses;

    if (date) {
      filtered = filtered.filter((expense) => {
        const expenseDate = new Date(expense.date).toISOString().split("T")[0];
        return expenseDate === date;
      });
    }

    if (category) {
      filtered = filtered.filter((expense) => expense.category === category);
    }

    if (project) {
      filtered = filtered.filter((expense) => expense.project === project);
    }

    setFilteredExpenses(filtered);
  };

  const handleRefresh = () => {
    fetchExpenses();
    setFilterDate("");
    setFilterCategory("");
    setFilterProject("");
  };

  const totalAmount = filteredExpenses.reduce(
    (acc, expense) => acc + expense.price,
    0
  );
  const uniqueCategories = [
    ...new Set(filteredExpenses.map((expense) => expense.category)),
  ];
  const totalTitles = filteredExpenses.length;

  return (
    <div className="bg-white h-full p-2">
      <div className="mb-4 flex flex-col md:flex-row md:justify-between md:items-center">
        <div className="mb-4 md:mb-0">
          <label
            htmlFor="filterDate"
            className="block text-sm font-bold text-themeColor2 mb-2"
          >
            Filter by Date:
          </label>
          <input
            type="date"
            id="filterDate"
            value={filterDate}
            onChange={handleFilterDateChange}
            className="border border-gray-300 rounded-md p-2 w-full md:w-auto"
          />
        </div>

        <button
          onClick={handleRefresh}
          className="bg-themeColor1 text-themeColor2 px-4 py-2 rounded-md mb-4 md:mb-0"
        >
          <i className="fa-solid fa-arrows-rotate"></i>
        </button>

        <div className="flex gap-2 w-full md:w-auto">
          <div>
            <label
              htmlFor="filterCategory"
              className="block text-sm font-bold text-themeColor2 mb-2"
            >
              Filter by Project:
            </label>
            <select
              id="filterProject"
              value={filterProject}
              onChange={handleFilterProjectChange}
              className="border border-gray-300 rounded-md p-2 w-full md:w-auto"
            >
              <option value="">Select Project</option>
              {projects.map((pro) => (
                <option key={pro._id} value={pro.project}>
                  {pro.project}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="filterCategory"
              className="block text-sm font-bold text-themeColor2 mb-2"
            >
              Filter by Category:
            </label>
            <select
              id="filterCategory"
              value={filterCategory}
              onChange={handleFilterCategoryChange}
              className="border border-gray-300 rounded-md p-2 w-full md:w-auto"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat.category}>
                  {cat.category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-48 mt-20">
            <div className="loader">
              <div className="newtons-cradle">
                <div className="newtons-cradle__dot"></div>
                <div className="newtons-cradle__dot"></div>
                <div className="newtons-cradle__dot"></div>
                <div className="newtons-cradle__dot"></div>
              </div>
            </div>
          </div>
        ) : error ? (
          <p>Error: {error}</p>
        ) : (
          <div className="relative">
            <div className="overflow-y-auto" style={{ maxHeight: "500px" }}>
              <table className="min-w-full divide-y divide-gray-200 border">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredExpenses.length > 0 ? (
                    filteredExpenses.map((expense) => (
                      <tr key={expense._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {expense.project}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {expense.category}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {expense.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          Rs. {expense.price.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(expense.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {userNames[expense.userId] || "Unknown"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {expense.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex gap-2">
                          <i
                            className="fa-regular fa-pen-to-square cursor-pointer"
                            onClick={() => openModal(expense)}
                          ></i>
                          <Popconfirm
                            title="Are you sure you want to delete?"
                            onConfirm={() => deleteExpenseById(expense._id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <i className="fa-solid fa-trash-can"></i>
                          </Popconfirm>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-center px-6 py-4 text-gray-500"
                      >
                        <i className="fa-solid fa-box-open text-gray-400 text-4xl mb-2"></i>
                        <p>No data available</p>
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredExpenses.length > 0 && (
                  <tfoot className="bg-gray-100 sticky bottom-0 z-10">
                    <tr>
                      <td className="px-6 py-4 font-bold bg-themeColor2 text-themeColor1">
                        {uniqueCategories.length} Projects
                      </td>
                      <td className="px-6 py-4 font-bold bg-themeColor2 text-themeColor1">
                        {uniqueCategories.length} Categories
                      </td>
                      <td className="px-6 py-4 font-bold bg-themeColor2 text-themeColor1">
                        {totalTitles} Items
                      </td>
                      <td
                        className="px-6 py-4 font-bold bg-themeColor2 text-themeColor1"
                        colSpan="5"
                      >
                        Rs. {totalAmount.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
      {/* Modal for Editing Ledger */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg w-80">
            <div className="">
              <h2 className="text-xl font-semibold mb-4">Edit Expense</h2>
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">Title</label>
                <input
                  type="text"
                  value={currentExpense.title}
                  onChange={(e) =>
                    setCurrentExpense({
                      ...currentExpense,
                      title: e.target.value,
                    })
                  }
                  className="border border-gray-300 rounded-md p-2 w-full"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">Amount</label>
                <input
                  type="text"
                  value={currentExpense.price}
                  onChange={(e) =>
                    setCurrentExpense({
                      ...currentExpense,
                      price: e.target.value,
                    })
                  }
                  className="border border-gray-300 rounded-md p-2 w-full"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  value={currentExpense.description}
                  onChange={(e) =>
                    setCurrentExpense({
                      ...currentExpense,
                      description: e.target.value,
                    })
                  }
                  className="border border-gray-300 rounded-md p-2 w-full"
                />
              </div>
              {/* Other fields like price, person, type, etc. */}
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">
                  Upload Images
                </label>
                <input
                  type="file"
                  onChange={handleImageChange}
                  className="border border-gray-300 rounded-md p-2"
                  multiple
                />
                <div className="mt-2 flex gap-2 flex-wrap">
                  {Array.isArray(currentExpense.imagePath) &&
                    currentExpense.imagePath.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Expense ${index}`}
                        className="w-20 h-20 object-cover rounded-md"
                      />
                    ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  className="bg-themeColor1 text-white px-4 py-2 rounded-md"
                  onClick={updateExpense}
                >
                  Save
                </button>
                <button
                  className="ml-2 bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
                  onClick={closeModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default ExpenseList;
